import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "./index.css";
import App from "./App.tsx";
import ErrorBoundary from "./components/ErrorBoundary";
import { lazyWithReload } from "./utils/lazyWithReload";
import { GUIDE_SLUGS, LOCALIZED_SLUGS } from "./guides/slugs";

import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

/* Route chunks load through lazyWithReload, which reloads a stale tab once
   instead of showing an error when a deploy has replaced its chunk names. */
const lazyRoute = lazyWithReload;

const Features = lazyRoute(() => import("./pages/Features"));
const Status = lazyRoute(() => import("./pages/Status"));
const Creators = lazyRoute(() => import("./pages/Creators"));
const AdminDailyGame = lazyRoute(() => import("./pages/admin/AdminDailyGame"));
const Products = lazyRoute(() => import("./pages/Products"));
const Product = lazyRoute(() => import("./pages/Product"));
const Pricing = lazyRoute(() => import("./pages/Pricing"));
const Login = lazyRoute(() => import("./pages/Login"));
const Signup = lazyRoute(() => import("./pages/Signup"));
const VerifyEmail = lazyRoute(() => import("./pages/VerifyEmail"));
const EmailVerified = lazyRoute(() => import("./pages/EmailVerified"));
const Invoice = lazyRoute(() => import("./pages/Invoice"));
const Downloads = lazyRoute(() => import("./pages/Downloads"));
const PayPalSuccess = lazyRoute(() => import("./pages/PayPalSuccess"));
const CryptoSuccess = lazyRoute(() => import("./pages/CryptoSuccess"));
const PaymentSuccess = lazyRoute(() => import("./pages/PaymentSuccess"));
const Admin = lazyRoute(() => import("./pages/Admin"));
const MediaTeam = lazyRoute(() => import("./pages/MediaTeam"));
const Moderator = lazyRoute(() => import("./pages/Moderator"));
const Profile = lazyRoute(() => import("./pages/Profile"));
const Terms = lazyRoute(() => import("./pages/Terms"));
const Privacy = lazyRoute(() => import("./pages/Privacy"));
const LuaDocs = lazyRoute(() => import("./pages/LuaDocs"));
const LuaSources = lazyRoute(() => import("./pages/LuaSources"));
const Redeem = lazyRoute(() => import("./pages/Redeem"));
const Guide = lazyRoute(() => import("./pages/Guide"));

const AdminUsers = lazyRoute(() => import("./pages/admin/AdminUsers"));
const AdminRevenue = lazyRoute(() => import("./pages/admin/AdminRevenue"));
const AdminPayments = lazyRoute(() => import("./pages/admin/AdminPayments"));
const AdminMediaTeam = lazyRoute(() => import("./pages/admin/AdminMediaTeam"));
const AdminSettings = lazyRoute(() => import("./pages/admin/AdminSettings"));
const AdminUpload = lazyRoute(() => import("./pages/admin/AdminUpload"));
const AdminDiagnostics = lazyRoute(() => import("./pages/admin/AdminDiagnostics"));
const AdminCrashes = lazyRoute(() => import("./pages/admin/AdminCrashes"));
const AdminRedeem = lazyRoute(() => import("./pages/admin/AdminRedeem"));
const AdminGrant = lazyRoute(() => import("./pages/admin/AdminGrant"));
const AdminNotes = lazyRoute(() => import("./pages/admin/AdminNotes"));
const AdminSecurity = lazyRoute(() => import("./pages/admin/AdminSecurity"));
const AdminHeatmap = lazyRoute(() => import("./pages/admin/AdminHeatmap"));

const PageLoader = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ background: "var(--color-ground)" }}
  >
    <div className="animate-pulse text-white/60">Loading...</div>
  </div>
);

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      {/* Inside the router so its "Go home" is a real way out, and outside
          Suspense so a lazy chunk that fails to render is caught too. */}
      <ErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<App />}>
            <Route index element={<Home />} />
            <Route path="features" element={<Features />} />
            <Route path="status" element={<Status />} />
            <Route path="creators" element={<Creators />} />
            <Route path="products" element={<Products />} />
            <Route path="product" element={<Product />} />
            {/* The canonical product URL. /product?game=<id> above still works
                and forwards here. */}
            <Route path="product/:gameId" element={<Product />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="downloads" element={<Downloads />} />
            <Route path="invoice/:invoiceId" element={<Invoice />} />
            <Route path="verify-email" element={<VerifyEmail />} />
            <Route path="email-verified" element={<EmailVerified />} />
            <Route path="paypal-success" element={<PayPalSuccess />} />
            <Route path="crypto-success" element={<CryptoSuccess />} />
            <Route path="payment/success" element={<PaymentSuccess />} />
            <Route
              path="downloads/payment/success"
              element={<PaymentSuccess />}
            />
            <Route path="login" element={<Login />} />
            <Route path="signup" element={<Signup />} />
            <Route path="media-team" element={<MediaTeam />} />
            <Route path="moderator" element={<Moderator />} />
            <Route path="profile" element={<Profile />} />
            <Route path="lua-docs" element={<LuaDocs />} />
            {/* Public, unlike the reference above it: the scripts are open source. */}
            <Route path="lua-docs/sources" element={<LuaSources />} />
            <Route path="terms" element={<Terms />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="redeem" element={<Redeem />} />
            {[...GUIDE_SLUGS, ...LOCALIZED_SLUGS].map((slug) => (
              <Route key={slug} path={slug} element={<Guide />} />
            ))}
          </Route>

          <Route path="admin" element={<Admin />} />
          <Route path="admin/users" element={<AdminUsers />} />
          <Route path="admin/subscriptions" element={<AdminUsers />} />
          <Route path="admin/bans" element={<AdminUsers />} />
          <Route path="admin/revenue" element={<AdminRevenue />} />
          <Route path="admin/revenue-logs" element={<AdminRevenue />} />
          <Route path="admin/payments" element={<AdminPayments />} />
          <Route path="admin/media-team" element={<AdminMediaTeam />} />
          <Route path="admin/referral-stats" element={<AdminMediaTeam />} />
          <Route path="admin/settings" element={<AdminSettings />} />
          <Route path="admin/upload" element={<AdminUpload />} />
        <Route path="admin/diagnostics" element={<AdminDiagnostics />} />
          <Route path="admin/crashes" element={<AdminCrashes />} />
          <Route path="admin/redeem" element={<AdminRedeem />} />
          <Route path="admin/grant" element={<AdminGrant />} />
          <Route path="admin/notes" element={<AdminNotes />} />
          <Route path="admin/daily-game" element={<AdminDailyGame />} />
          <Route path="admin/security" element={<AdminSecurity />} />
          <Route path="admin/heatmap" element={<AdminHeatmap />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  </StrictMode>
);
